import type { CSSProperties } from 'react';

/**
 * Turns a raw CSS declaration string into a React style object.
 *
 * `options.layout.style` and `options.badgeStyles[].style` arrive from config JSON as
 * strings — `"backdrop-filter: blur(10px);"` — because the legacy app pasted them straight
 * into a Vue `:style` binding, which accepts strings. React does not, so the string is
 * parsed here rather than injected.
 *
 * The parser is deliberately forgiving: config files are edited by hand and deployed to
 * unattended displays, so a stray semicolon must cost one declaration, never the screen
 * (CLAUDE.md constraint 4). Nothing here throws.
 *
 * Known limit: splitting on `;` would also split inside a `url("data:…;base64,…")` value.
 * No deployed config contains one, and handling it properly means a real tokeniser; if a
 * data URI ever appears in a config, that is the moment to write one.
 */
export function parseStyleString(declarations: string | undefined | null): CSSProperties {
  const style: Record<string, string> = {};
  if (!declarations) return style;

  for (const segment of declarations.split(';')) {
    // Split on the *first* colon only — values contain them (`url(https://…)`, `var(…)`).
    const separator = segment.indexOf(':');
    if (separator === -1) continue;

    const property = segment.slice(0, separator).trim();
    const value = segment.slice(separator + 1).trim();
    if (property === '' || value === '') continue;

    style[toStyleKey(property)] = value;
  }

  // The keys are only known at runtime, so the accumulator is a plain record. React's
  // `CSSProperties` accepts it directly — no cast — because it carries an index signature
  // for custom properties, which is also what lets a parsed `--foo` reach the DOM.
  return style;
}

/**
 * `background-color` -> `backgroundColor`, `-webkit-mask` -> `WebkitMask`.
 *
 * Custom properties pass through untouched: React writes any key starting with `--` via
 * `setProperty`, and camel-casing one would silently drop the declaration.
 *
 * CSS property names are case-insensitive, so the name is lowercased before conversion —
 * `Backdrop-Filter` from a hand-edited config still reaches React as `backdropFilter`.
 * The one prefix this gets wrong is `-ms-`, which React spells `msTransform` rather than
 * `MsTransform`; no config uses it, and Chromium needs no `-ms-` prefixes.
 */
function toStyleKey(property: string): string {
  if (property.startsWith('--')) return property;
  return property
    .toLowerCase()
    .replace(/-([a-z])/g, (_, character: string) => character.toUpperCase());
}
